// TOTP multi-factor enrollment + recovery codes.
// The server only marks mfa_enrolled after it sees an AAL2 token, so nothing
// here can grant assurance on its own.
import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, KeyRound, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { mfa, currentAal } from '@/lib/accountSecurity';

const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'ghost' }> = ({
  tone = 'primary',
  className = '',
  ...p
}) => (
  <button
    {...p}
    className={`rounded-lg px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${
      tone === 'primary' ? 'bg-slate-900 text-white hover:bg-slate-800' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
    } ${className}`}
  />
);

const MfaEnrollment: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const [status, setStatus] = useState<any>(null);
  const [aal, setAal] = useState('aal1');
  const [factorId, setFactorId] = useState('');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await mfa.status();
    setStatus(data);
    setAal(await currentAal());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startEnroll = async () => {
    setBusy(true);
    setMsg('');
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: `GIGGONE ${Date.now()}` });
      if (error) throw error;
      setFactorId(data.id);
      setQr(data.totp?.qr_code || '');
      setSecret(data.totp?.secret || '');
    } catch (e: any) {
      setMsg(e?.message || 'Could not start enrollment.');
    }
    setBusy(false);
  };

  const verify = async () => {
    if (!factorId || code.trim().length < 6) return;
    setBusy(true);
    setMsg('');
    try {
      const { data: ch, error: e1 } = await supabase.auth.mfa.challenge({ factorId });
      if (e1) throw e1;
      const { error: e2 } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code: code.trim() });
      if (e2) throw e2;
      const { data, error } = await mfa.confirmEnrollment();
      if (error) throw new Error(error);
      setCodes(data?.recovery_codes || []);
      setQr('');
      setSecret('');
      setCode('');
      setMsg('Multi-factor authentication is active on this account.');
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Verification failed.');
    }
    setBusy(false);
  };

  const stepUp = async () => {
    setBusy(true);
    setMsg('');
    try {
      const { data: list } = await supabase.auth.mfa.listFactors();
      const f = (list?.totp || [])[0];
      if (!f) throw new Error('No enrolled factor found.');
      const { data: ch, error: e1 } = await supabase.auth.mfa.challenge({ factorId: f.id });
      if (e1) throw e1;
      const { error: e2 } = await supabase.auth.mfa.verify({ factorId: f.id, challengeId: ch.id, code: code.trim() });
      if (e2) throw e2;
      setCode('');
      setMsg('Second factor accepted for this session.');
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Challenge failed.');
    }
    setBusy(false);
  };

  const enrolled = !!status?.mfa_enrolled;
  const enforced = !!status?.enforced;

  return (
    <section className={compact ? '' : 'rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            {enrolled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
            Multi-factor authentication
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {enrolled
              ? `Enrolled. Current session assurance: ${aal.toUpperCase()}.`
              : 'Protect this account with an authenticator app (TOTP).'}
            {status?.is_admin && (
              <span className="ml-1 font-medium text-slate-800">
                {enforced ? 'Required for administrators.' : 'Required for administrators once enforcement is switched on.'}
              </span>
            )}
          </p>
        </div>
        <Btn tone="ghost" onClick={() => void load()} disabled={busy} aria-label="Refresh MFA status">
          <RefreshCw className="h-4 w-4" />
        </Btn>
      </div>

      {!enrolled && !qr && (
        <Btn className="mt-4" onClick={startEnroll} disabled={busy}>
          Set up authenticator app
        </Btn>
      )}

      {qr && (
        <div className="mt-4 grid gap-4 md:grid-cols-[200px,1fr]">
          <img src={qr} alt="Authenticator QR code" className="h-48 w-48 rounded-lg border border-slate-200 bg-white p-2" />
          <div>
            <p className="text-sm text-slate-600">
              Scan the code, or enter this setup key manually:
              <code className="ml-1 rounded bg-slate-100 px-2 py-0.5 text-xs tracking-wider">{secret}</code>
            </p>
            <div className="mt-3 flex gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                placeholder="6-digit code"
                className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <Btn onClick={verify} disabled={busy}>
                Verify &amp; activate
              </Btn>
            </div>
          </div>
        </div>
      )}

      {enrolled && aal !== 'aal2' && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">This session has not completed the second factor.</p>
          <div className="mt-2 flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              placeholder="6-digit code"
              className="w-40 rounded-lg border border-amber-300 px-3 py-2 text-sm"
            />
            <Btn onClick={stepUp} disabled={busy}>
              Confirm second factor
            </Btn>
          </div>
        </div>
      )}

      {codes.length > 0 && (
        <div className="mt-4 rounded-lg border border-slate-300 bg-slate-50 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <KeyRound className="h-4 w-4" /> Recovery codes — shown once, store them offline
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {codes.map((c) => (
              <code key={c} className="rounded bg-white px-2 py-1 text-center text-sm tracking-widest text-slate-800">
                {c}
              </code>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-600">
            Each code works once and forces a fresh enrollment afterwards. Every use is logged.
          </p>
        </div>
      )}

      {enrolled && status?.recovery_codes_remaining >= 0 && codes.length === 0 && (
        <p className="mt-3 text-xs text-slate-500">{status.recovery_codes_remaining} unused recovery code(s) remaining.</p>
      )}

      {msg && <p className="mt-3 text-sm text-slate-700">{msg}</p>}
    </section>
  );
};

export default MfaEnrollment;
